import { useState } from 'react'
import type { PresetBackground } from '../data/presetBackgrounds'

interface PresetBackgroundButtonProps {
  preset: PresetBackground
  isSelected: boolean
  isLoading: boolean
  onClick: () => void
}

export function PresetBackgroundButton({
  preset,
  isSelected,
  isLoading,
  onClick,
}: PresetBackgroundButtonProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={`relative w-full aspect-square rounded-lg overflow-hidden transition-all group ${
        isSelected
          ? 'ring-2 ring-primary ring-offset-2 shadow-md'
          : 'border border-border-color hover:border-primary/50 hover:shadow-sm'
      } ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
      title={preset.name}
    >
      {/* Background Image or Fallback Color */}
      {!imageError ? (
        <img
          src={preset.url}
          alt={preset.name}
          className={`w-full h-full object-cover transition-opacity ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      ) : null}

      {/* Fallback color (shown while loading or on error) */}
      {(!imageLoaded || imageError) && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: preset.fallbackColor }}
        >
          {imageError && (
            <span className="material-symbols-outlined text-lg text-text-muted/50">
              image
            </span>
          )}
        </div>
      )}

      {/* Hover Overlay with Name */}
      <div
        className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity ${
          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        <span className="text-white text-xs font-medium text-center px-1 leading-tight">
          {preset.name}
        </span>
      </div>

      {/* Selected Checkmark */}
      {isSelected && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
          <span className="material-symbols-outlined text-white text-sm">check</span>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && isSelected && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </button>
  )
}
